import React, { useCallback, useEffect, useState } from "react";
import { MapPin, Plus, Pencil, Trash2, ChevronRight } from "lucide-react";
import api from "../../services/api";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { useToast } from "../../components/ui/use-toast";

const emptyForm = { name: "", is_active: true, sort_order: 0 };

const Locations = () => {
  const { toast } = useToast();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [selectedStateId, setSelectedStateId] = useState(null);

  const [dialog, setDialog] = useState(null);
  // dialog: { type: 'country'|'state'|'city', mode: 'create'|'edit', item?, parentId? }
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/locations");
      const data = res.data?.data || [];
      setTree(data);
      if (data.length && !selectedCountryId) {
        setSelectedCountryId(data[0].id);
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to load locations",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedCountryId, toast]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedCountry = tree.find((c) => c.id === selectedCountryId);
  const states = selectedCountry?.states || [];
  const selectedState = states.find((s) => s.id === selectedStateId);
  const cities = selectedState?.cities || [];

  useEffect(() => {
    if (!selectedCountry) return;
    if (
      !selectedStateId ||
      !states.some((s) => s.id === selectedStateId)
    ) {
      setSelectedStateId(states[0]?.id || null);
    }
  }, [selectedCountry, selectedStateId, states]);

  const openCreate = (type, parentId) => {
    setForm(emptyForm);
    setDialog({ type, mode: "create", parentId });
  };

  const openEdit = (type, item) => {
    setForm({
      name: item.name,
      is_active: item.is_active,
      sort_order: item.sort_order ?? 0,
    });
    setDialog({ type, mode: "edit", item });
  };

  const endpointFor = (type, id) => {
    if (type === "country") {
      return id ? `/admin/locations/countries/${id}` : "/admin/locations/countries";
    }
    if (type === "state") {
      return id ? `/admin/locations/states/${id}` : "/admin/locations/states";
    }
    return id ? `/admin/locations/cities/${id}` : "/admin/locations/cities";
  };

  const handleSave = async () => {
    if (!dialog) return;
    const name = form.name.trim();
    if (!name) {
      toast({
        title: "Error",
        description: "Name is required",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name,
        is_active: form.is_active,
        sort_order: Number(form.sort_order) || 0,
      };

      if (dialog.mode === "create") {
        if (dialog.type === "state") payload.country_id = dialog.parentId;
        if (dialog.type === "city") payload.state_id = dialog.parentId;
        await api.post(endpointFor(dialog.type), payload);
      } else {
        await api.put(endpointFor(dialog.type, dialog.item.id), payload);
      }

      toast({ title: "Success", description: "Saved successfully" });
      setDialog(null);
      await load();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Save failed",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, item) => {
    if (
      !window.confirm(
        `Delete ${type} "${item.name}"? This also removes nested items.`
      )
    ) {
      return;
    }
    try {
      await api.delete(endpointFor(type, item.id));
      toast({ title: "Success", description: "Deleted" });
      if (type === "country" && selectedCountryId === item.id) {
        setSelectedCountryId(null);
      }
      if (type === "state" && selectedStateId === item.id) {
        setSelectedStateId(null);
      }
      await load();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Delete failed",
        variant: "destructive",
      });
    }
  };

  const ListPanel = ({
    title,
    items,
    selectedId,
    onSelect,
    onAdd,
    onEdit,
    onDelete,
    emptyText,
  }) => (
    <Card className="flex-1 min-w-0">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
        <Button size="sm" variant="outline" onClick={onAdd} disabled={!onAdd}>
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </CardHeader>
      <CardContent className="space-y-1 max-h-[60vh] overflow-y-auto">
        {items.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {emptyText}
          </p>
        )}
        {items.map((item) => (
          <div
            key={item.id}
            className={`flex items-center gap-2 rounded-md border px-2 py-1.5 cursor-pointer ${
              selectedId === item.id
                ? "border-primary bg-primary/5"
                : "border-transparent hover:bg-muted/50"
            }`}
            onClick={() => onSelect?.(item.id)}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate">{item.name}</span>
                {!item.is_active && (
                  <Badge variant="secondary" className="text-[10px]">
                    Off
                  </Badge>
                )}
              </div>
            </div>
            {onSelect && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(item);
              }}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(item);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <MapPin className="h-7 w-7" />
            Locations
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage countries, states, and cities shown as suggestions in the
            mobile app. Users can still type a custom value.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-4">
          <ListPanel
            title={`Countries (${tree.length})`}
            items={tree}
            selectedId={selectedCountryId}
            onSelect={setSelectedCountryId}
            onAdd={() => openCreate("country")}
            onEdit={(item) => openEdit("country", item)}
            onDelete={(item) => handleDelete("country", item)}
            emptyText="No countries yet"
          />
          <ListPanel
            title={`States (${states.length})`}
            items={states}
            selectedId={selectedStateId}
            onSelect={setSelectedStateId}
            onAdd={
              selectedCountryId
                ? () => openCreate("state", selectedCountryId)
                : null
            }
            onEdit={(item) => openEdit("state", item)}
            onDelete={(item) => handleDelete("state", item)}
            emptyText={
              selectedCountry
                ? "No states for this country"
                : "Select a country"
            }
          />
          <ListPanel
            title={`Cities (${cities.length})`}
            items={cities}
            selectedId={null}
            onSelect={null}
            onAdd={
              selectedStateId
                ? () => openCreate("city", selectedStateId)
                : null
            }
            onEdit={(item) => openEdit("city", item)}
            onDelete={(item) => handleDelete("city", item)}
            emptyText={
              selectedState ? "No cities for this state" : "Select a state"
            }
          />
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog?.mode === "create" ? "Add" : "Edit"}{" "}
              {dialog?.type || ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="loc-name">Name</Label>
              <Input
                id="loc-name"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="e.g. Maharashtra"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="loc-sort">Sort order</Label>
              <Input
                id="loc-sort"
                type="number"
                value={form.sort_order}
                onChange={(e) =>
                  setForm((f) => ({ ...f, sort_order: e.target.value }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="loc-active">Active (shown in app)</Label>
              <Switch
                id="loc-active"
                checked={form.is_active}
                onCheckedChange={(v) =>
                  setForm((f) => ({ ...f, is_active: v }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Locations;
